import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import type { ApiError, ApiSuccess, ResponseMeta, TokenPair } from './types';

const ACCESS_TOKEN_KEY = 'oro.access';
const REFRESH_TOKEN_KEY = 'oro.refresh';

export const tokenStore = {
  get access(): string | null {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  },
  get refresh(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  },
  set(pair: TokenPair): void {
    localStorage.setItem(ACCESS_TOKEN_KEY, pair.accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, pair.refreshToken);
  },
  clear(): void {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  },
};

/** Raised for any non-2xx response, carrying the server's error envelope. */
export class ApiRequestError extends Error {
  readonly code: ApiError['error']['code'];
  readonly details: ApiError['error']['details'];
  readonly status: number;
  readonly requestId?: string;

  constructor(status: number, payload: ApiError) {
    super(payload.error.message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = payload.error.code;
    this.details = payload.error.details;
    this.requestId = payload.requestId;
  }

  /** Field errors keyed by name, for react-hook-form's setError. */
  get fieldErrors(): Record<string, string> {
    return Object.fromEntries((this.details ?? []).map((d) => [d.field, d.message]));
  }
}

const client: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL ?? '/api/v1',
  headers: { 'Content-Type': 'application/json' },
});

client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = tokenStore.access;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/**
 * A single in-flight refresh shared by every request that gets a 401, so a
 * page that fires six queries at once refreshes once rather than six times and
 * rotates the token out from under itself.
 */
let refreshInFlight: Promise<TokenPair> | null = null;

async function refreshTokens(): Promise<TokenPair> {
  const refreshToken = tokenStore.refresh;
  if (!refreshToken) throw new Error('No refresh token');

  refreshInFlight ??= axios
    .post<ApiSuccess<TokenPair>>(
      `${client.defaults.baseURL}/auth/refresh`,
      { refreshToken },
      { headers: { 'Content-Type': 'application/json' } },
    )
    .then((response) => {
      const pair = response.data.data;
      tokenStore.set(pair);
      return pair;
    })
    .finally(() => {
      refreshInFlight = null;
    });

  return refreshInFlight;
}

type RetriableConfig = InternalAxiosRequestConfig & { _retried?: boolean };

client.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiError>) => {
    const config = error.config as RetriableConfig | undefined;
    const status = error.response?.status;

    // One retry after a refresh; never on the refresh call itself.
    if (
      status === 401 &&
      config &&
      !config._retried &&
      !config.url?.includes('/auth/refresh') &&
      !config.url?.includes('/auth/login') &&
      tokenStore.refresh
    ) {
      config._retried = true;
      try {
        const pair = await refreshTokens();
        config.headers.Authorization = `Bearer ${pair.accessToken}`;
        return client.request(config);
      } catch {
        tokenStore.clear();
        // Full reload so every cache and store resets cleanly.
        if (!window.location.pathname.startsWith('/login')) {
          window.location.href = '/login';
        }
      }
    }

    if (error.response?.data?.error) {
      throw new ApiRequestError(status ?? 500, error.response.data);
    }

    throw new ApiRequestError(status ?? 0, {
      success: false,
      error: {
        code: 'INTERNAL_ERROR',
        message: error.message || 'Could not reach the server',
      },
      requestId: 'client',
    });
  },
);

/** GET returning a bare payload. */
export async function apiGet<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const response = await client.get<ApiSuccess<T>>(url, config);
  return response.data.data;
}

/** GET returning a paginated list plus its meta. */
export async function apiList<T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<{ data: T; meta: ResponseMeta }> {
  const response = await client.get<ApiSuccess<T>>(url, config);
  return {
    data: response.data.data,
    meta: response.data.meta ?? { page: 1, limit: 25, total: 0, totalPages: 1 },
  };
}

export async function apiPost<T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const response = await client.post<ApiSuccess<T>>(url, body, config);
  return response.data?.data;
}

export async function apiPatch<T>(url: string, body?: unknown): Promise<T> {
  const response = await client.patch<ApiSuccess<T>>(url, body);
  return response.data.data;
}

export async function apiDelete(url: string): Promise<void> {
  await client.delete(url);
}

export { client as apiClient };
